/**
 * Desktop wallpaper catalog
 *
 * Each entry has a light and dark variant URL (Pexels, free to use).
 * The "default" entry matches the original hardcoded backgrounds.
 */

export interface WallpaperOption {
  id: string;
  label: string;
  darkUrl: string;
  lightUrl: string;
  /** Small thumbnail-sized URL for the picker preview */
  darkThumb: string;
  lightThumb: string;
}

export const pexelsUrl = (id: number | string, w = 1920) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}`;

const pexels = (id: number, w = 1920) => pexelsUrl(id, w);

export const WALLPAPERS: WallpaperOption[] = [
  {
    id: "default",
    label: "Default",
    darkUrl: pexels(34720596),
    lightUrl: pexels(691668),
    darkThumb: pexels(34720596, 400),
    lightThumb: pexels(691668, 400),
  },
  {
    id: "mountains",
    label: "Mountains",
    darkUrl: pexels(1261728),
    lightUrl: pexels(417173),
    darkThumb: pexels(1261728, 400),
    lightThumb: pexels(417173, 400),
  },
  {
    id: "ocean",
    label: "Ocean",
    darkUrl: pexels(1118873),
    lightUrl: pexels(994605),
    darkThumb: pexels(1118873, 400),
    lightThumb: pexels(994605, 400),
  },
  {
    id: "forest",
    label: "Forest",
    darkUrl: pexels(1612351),
    lightUrl: pexels(167698),
    darkThumb: pexels(1612351, 400),
    lightThumb: pexels(167698, 400),
  },
  {
    id: "abstract",
    label: "Abstract",
    darkUrl: pexels(1629236),
    lightUrl: pexels(7134995),
    darkThumb: pexels(1629236, 400),
    lightThumb: pexels(7134995, 400),
  },
  {
    id: "city",
    label: "City",
    darkUrl: pexels(1519088),
    lightUrl: pexels(466685),
    darkThumb: pexels(1519088, 400),
    lightThumb: pexels(466685, 400),
  },
];

export function getWallpaper(
  id?: string,
  customPexelsIds?: string[]
): WallpaperOption | null {
  if (!id) return WALLPAPERS[0];

  // Check built-in wallpapers first
  const builtIn = WALLPAPERS.find((w) => w.id === id);
  if (builtIn) return builtIn;

  // Check custom pexels backgrounds (stored as "pexels:{numericId}")
  if (id.startsWith("pexels:")) {
    const numericId = id.slice(7);
    if (customPexelsIds?.includes(numericId)) {
      return {
        id,
        label: `Pexels #${numericId}`,
        darkUrl: pexelsUrl(numericId),
        lightUrl: pexelsUrl(numericId),
        darkThumb: pexelsUrl(numericId, 400),
        lightThumb: pexelsUrl(numericId, 400),
      };
    }
  }

  return WALLPAPERS[0];
}

/**
 * Parse a Pexels photo ID from a URL or raw ID string.
 * Returns the numeric ID string or null if invalid.
 *
 * Supported formats:
 *  - "12345" (raw numeric ID)
 *  - "https://www.pexels.com/photo/some-description-12345/"
 *  - "https://www.pexels.com/photo/12345/"
 *  - "https://www.pexels.com/photo/12345"
 */
export function parsePexelsInput(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Raw numeric ID
  if (/^\d+$/.test(trimmed)) return trimmed;

  // URL pattern — grab trailing numeric segment
  const match = trimmed.match(/pexels\.com\/photo\/(?:[\w-]+-)?(\d+)\/?$/);
  if (match) return match[1];

  return null;
}
